import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Smartphone, Share, Plus, Bell, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'boufi_pwa_prompt_dismissed_at';
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isMobile() {
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

export default function InstallPWAPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const ios = isIOS();

  useEffect(() => {
    if (isStandalone()) return; // deja installee
    if (!isMobile()) return; // pas la peine sur desktop
    const dismissed = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    const now = Date.now();
    if (now - dismissed < DISMISS_DAYS * 24 * 3600 * 1000) return;

    // Capture beforeinstallprompt for Android/Chrome
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Show after 3s to not spam at load
    const t = setTimeout(() => setShow(true), 3000);
    return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', handler); };
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
  };

  const androidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 26 }}
        className="fixed inset-x-3 bottom-3 z-[100] md:left-auto md:right-4 md:max-w-sm"
        data-testid="install-pwa-prompt"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="bg-surface-elev rounded-2xl shadow-2xl border border-[#EEE7DA] overflow-hidden">
          {/* Header */}
          <div className="px-4 pt-4 pb-3 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #FF8C40, #FFB47A)' }}>
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm text-[#1A1A1A]">Installer Boufi OS</div>
              <div className="text-xs text-[#8B7355] mt-0.5">Notifications instantanées même app fermée 🔔</div>
            </div>
            <button onClick={dismiss} className="p-1.5 rounded-lg hover:bg-black/5 -mt-1 -mr-1 flex-shrink-0">
              <X className="w-4 h-4 text-[#9C9285]" />
            </button>
          </div>

          {/* Benefits */}
          <div className="px-4 pb-3 flex gap-2 flex-wrap">
            {[
              { icon: Bell, text: 'Notifs Push' },
              { icon: Sparkles, text: 'Plein écran' },
              { icon: Smartphone, text: 'Icône Home' },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFF1E5] text-[10px] font-semibold text-[#7A4A1A]">
                  <Icon className="w-3 h-3" /> {b.text}
                </span>
              );
            })}
          </div>

          {/* Instructions iOS */}
          {ios ? (
            <div className="px-4 pb-4">
              <div className="bg-[#FAFAF7] rounded-xl p-3 border border-[#EEE7DA] space-y-2">
                <div className="text-xs font-bold text-[#1A1A1A] mb-1">📲 Sur iPhone :</div>
                <div className="flex items-center gap-2 text-xs text-[#4A3D2F]">
                  <span className="w-5 h-5 rounded-full bg-[#FF8C40] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                  <span>Appuie sur <Share className="inline w-3.5 h-3.5 -mt-0.5 text-[#007AFF]" /> en bas de Safari</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#4A3D2F]">
                  <span className="w-5 h-5 rounded-full bg-[#FF8C40] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                  <span>Choisis <b>« Sur l'écran d'accueil »</b> <Plus className="inline w-3.5 h-3.5 -mt-0.5" /></span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#4A3D2F]">
                  <span className="w-5 h-5 rounded-full bg-[#FF8C40] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                  <span>Appuie <b>« Ajouter »</b> ✅</span>
                </div>
              </div>
              <button
                onClick={dismiss}
                className="w-full mt-3 text-xs text-[#7A7060] hover:text-[#1A1A1A] py-1.5"
                data-testid="pwa-prompt-dismiss"
              >
                Plus tard
              </button>
            </div>
          ) : (
            <div className="px-4 pb-4 flex gap-2">
              <button
                onClick={dismiss}
                className="flex-1 px-3 py-2 rounded-xl border border-[#EEE7DA] text-[#7A7060] text-sm font-semibold hover:bg-black/5"
              >
                Plus tard
              </button>
              <button
                onClick={androidInstall}
                disabled={!deferredPrompt}
                data-testid="pwa-install-btn"
                className="flex-1 px-3 py-2 rounded-xl bg-[#FF8C40] hover:bg-[#FF7A22] text-white text-sm font-semibold transition disabled:opacity-50"
              >
                Installer
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
